// src/components/billing/VelasGate.tsx
// Acceso a velas industriales: pase diario en coins o suscripción B2B en COP.
import { useEffect, useState, type ReactNode } from 'react';
import { BarChart3, Loader2, Lock, Wallet } from 'lucide-react';
import { toast } from 'sonner';
import { hasFeaturePass, debitCoins } from '@/lib/wallet';
import { getActiveB2B, startB2BCheckout, B2B_PLANS, type B2BSubscription } from '@/lib/b2b';
import { WALLET_PRICES, formatCoins, B2B } from '@/config/pricing';

type Access = 'LOADING' | 'GRANTED' | 'DENIED';

const money = (cop: number) => `$${cop.toLocaleString('es-CO')} COP`;

export const VelasGate = ({ children }: { children: ReactNode }) => {
  const [access, setAccess] = useState<Access>('LOADING');
  const [sub, setSub] = useState<B2BSubscription | null>(null);
  const [busy, setBusy] = useState(false);

  const check = async () => {
    setAccess('LOADING');
    const [pass, active] = await Promise.all([hasFeaturePass('velas_daily', 24), getActiveB2B()]);
    setSub(active);
    setAccess(pass || active ? 'GRANTED' : 'DENIED');
  };

  useEffect(() => {
    void check();
  }, []);

  const buyDaily = async () => {
    setBusy(true);
    try {
      const day = new Date().toISOString().slice(0, 10);
      const result = await debitCoins({
        amount: WALLET_PRICES.VELAS_DAILY,
        sku: 'velas_daily',
        referenceId: day,
        idempotencyKey: `velas_daily:${day}`,
        metadata: { access: '24h' },
      });
      if (!result.ok) {
        toast.error(result.error || 'No se pudo comprar el pase');
        return;
      }
      toast.success(`Pase de velas activo por 24 h · saldo ${formatCoins(result.balance)}`);
      await check();
    } finally {
      setBusy(false);
    }
  };

  const buyB2B = async (plan: keyof typeof B2B_PLANS) => {
    setBusy(true);
    try {
      const result = await startB2BCheckout(plan);
      if (result.ok && result.url) {
        window.open(result.url, '_blank', 'noopener,noreferrer');
        return;
      }
      if (result.fallbackWhatsApp) {
        window.open(result.fallbackWhatsApp, '_blank', 'noopener,noreferrer');
        toast.info('Te atendemos por WhatsApp para emitir el link de pago B2B.');
      } else {
        toast.error(result.error || 'No se pudo iniciar el pago B2B');
      }
    } finally {
      setBusy(false);
    }
  };

  if (access === 'LOADING') {
    return (
      <div className="flex h-full items-center justify-center p-10">
        <Loader2 size={28} className="animate-spin text-indigo-500" />
      </div>
    );
  }

  if (access === 'GRANTED') {
    return (
      <div className="h-full">
        <div className="mx-4 mt-3 flex flex-wrap items-center justify-between gap-2 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-2 text-[11px] font-bold text-emerald-700 dark:border-emerald-900 dark:bg-emerald-950/30 dark:text-emerald-300">
          <span>
            {sub
              ? `Suscripción B2B activa hasta ${new Date(sub.expires_at).toLocaleDateString('es-CO')}`
              : 'Pase diario activo (24 h)'}
          </span>
          <span>Datos industriales anónimos</span>
        </div>
        {children}
      </div>
    );
  }

  return (
    <div className="flex h-full items-center justify-center p-6">
      <div className="w-full max-w-lg rounded-3xl border border-slate-200 bg-white p-8 shadow-xl dark:border-slate-800 dark:bg-slate-900">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-indigo-50 text-indigo-500 dark:bg-indigo-950/40">
          <Lock size={26} />
        </div>
        <h1 className="text-center text-2xl font-black text-slate-900 dark:text-white">Velas industriales B2B</h1>
        <p className="mt-2 text-center text-sm text-slate-500">
          Series anónimas de mercado por categoría, producto y ciudad. Elige pase diario o suscripción.
        </p>

        <button
          onClick={() => void buyDaily()}
          disabled={busy}
          className="mt-6 flex w-full items-center justify-between rounded-2xl border-2 border-indigo-200 bg-indigo-50 px-4 py-3 text-left hover:border-indigo-400 disabled:opacity-50 dark:border-indigo-900 dark:bg-indigo-950/30"
        >
          <span>
            <span className="flex items-center gap-1.5 text-sm font-black text-indigo-700 dark:text-indigo-300">
              <Wallet size={15} /> Pase diario
            </span>
            <span className="block text-[11px] text-slate-500">Acceso completo 24 horas</span>
          </span>
          <span className="text-sm font-black text-indigo-700 dark:text-indigo-300">{formatCoins(WALLET_PRICES.VELAS_DAILY)}</span>
        </button>

        <div className="mt-3 grid gap-2 sm:grid-cols-2">
          <button
            onClick={() => void buyB2B('VELAS_B2B_MONTHLY')}
            disabled={busy}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-indigo-300 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
          >
            <span className="flex items-center gap-1.5 text-sm font-black text-slate-800 dark:text-slate-100">
              <BarChart3 size={15} /> Mensual
            </span>
            <span className="block text-[11px] text-slate-500">{money(B2B.VELAS_MONTHLY_COP)} · 30 días</span>
          </button>
          <button
            onClick={() => void buyB2B('VELAS_B2B_ANNUAL')}
            disabled={busy}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-3 text-left hover:border-indigo-300 disabled:opacity-50 dark:border-slate-700 dark:bg-slate-900"
          >
            <span className="flex items-center gap-1.5 text-sm font-black text-slate-800 dark:text-slate-100">
              <BarChart3 size={15} /> Anual
            </span>
            <span className="block text-[11px] text-slate-500">{money(B2B.VELAS_ANNUAL_COP)} · 2 meses gratis</span>
          </button>
        </div>

        <p className="mt-4 text-center text-[10px] text-slate-400">
          El pase diario se cobra en coins. Las suscripciones B2B se facturan en COP y se activan cuando Wompi confirma el pago.
        </p>
      </div>
    </div>
  );
};

export default VelasGate;
